import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import { toast } from "react-toastify";

import { appointmentsApi } from "../Components/appointmentsApi";
import { UserContext } from "../Contexts/LoginContext";
import styles from "./AppointmentsSettingsPage.module.scss";

const weekdays = [
  { id: 0, label: "Monday" },
  { id: 1, label: "Tuesday" },
  { id: 2, label: "Wednesday" },
  { id: 3, label: "Thursday" },
  { id: 4, label: "Friday" },
  { id: 5, label: "Saturday" },
  { id: 6, label: "Sunday" },
];

function makeRule(day) {
  return {
    weekday: day.id,
    enabled: day.id < 5,
    start_time: "09:00",
    end_time: "17:00",
    slot_duration_minutes: 30,
    buffer_before_minutes: 0,
    buffer_after_minutes: 0,
    max_concurrent_bookings: 1,
  };
}

export default function AppointmentsSettingsPage() {
  const { username } = useContext(UserContext);
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");
  const [rules, setRules] = useState(weekdays.map(makeRule));
  const [exceptions, setExceptions] = useState([]);
  const [newException, setNewException] = useState({
    date: "",
    is_available: false,
    override_start_time: "",
    override_end_time: "",
    reason: "",
  });
  const [outlookStatus, setOutlookStatus] = useState(null);
  const [outlookBusy, setOutlookBusy] = useState(false);
  const [reminderBusy, setReminderBusy] = useState(false);
  const [outlookForm, setOutlookForm] = useState({
    email: "",
    tenant_id: "",
    calendar_id: "",
    access_token: "",
    refresh_token: "",
    token_expires_at: "",
  });

  const selectedCounsellor = useMemo(() => Number(username || 0), [username]);

  const loadRoster = async () => {
    if (!selectedCounsellor) {
      return;
    }
    const roster = await appointmentsApi.getRoster(selectedCounsellor);
    if (!roster?.error) {
      setTimezone(roster?.timezone || timezone);
      const incoming = Array.isArray(roster?.rules) ? roster.rules : [];
      const merged = weekdays.map((day) => {
        const found = incoming.find((item) => Number(item.weekday) === day.id);
        if (!found) {
          return makeRule(day);
        }
        return {
          ...makeRule(day),
          ...found,
          start_time: found.start_time?.slice(0, 5) || "09:00",
          end_time: found.end_time?.slice(0, 5) || "17:00",
        };
      });
      setRules(merged);
    }
  };

  const loadExceptions = async () => {
    if (!selectedCounsellor) {
      setExceptions([]);
      return;
    }
    const data = await appointmentsApi.listExceptions({ counsellor_id: selectedCounsellor });
    setExceptions(data?.exceptions || []);
  };

  useEffect(() => {
    loadRoster();
    loadExceptions();
  }, [selectedCounsellor]);

  const loadOutlookStatus = async () => {
    const data = await appointmentsApi.getOutlookStatus();
    if (data?.error) {
      return;
    }
    setOutlookStatus(data?.integration || null);
    if (data?.integration) {
      setOutlookForm((prev) => ({
        ...prev,
        email: data.integration.email || prev.email,
        tenant_id: data.integration.tenant_id || prev.tenant_id,
        calendar_id: data.integration.calendar_id || prev.calendar_id,
        token_expires_at: data.integration.token_expires_at || prev.token_expires_at,
      }));
    }
  };

  useEffect(() => {
    loadOutlookStatus();
  }, []);

  const updateRule = (weekday, field, value) => {
    setRules((prev) =>
      prev.map((rule) => (rule.weekday === weekday ? { ...rule, [field]: value } : rule))
    );
  };

  const saveRoster = async () => {
    if (!selectedCounsellor) {
      return;
    }
    const payload = {
      counsellor_id: selectedCounsellor,
      timezone,
      rules: rules.map((rule) => ({
        ...rule,
        enabled: Boolean(rule.enabled),
        slot_duration_minutes: Number(rule.slot_duration_minutes || 30),
        buffer_before_minutes: Number(rule.buffer_before_minutes || 0),
        buffer_after_minutes: Number(rule.buffer_after_minutes || 0),
        max_concurrent_bookings: Number(rule.max_concurrent_bookings || 1),
      })),
    };
    const data = await appointmentsApi.updateRoster(payload);
    if (data?.error) {
      toast.error(data.error);
      return;
    }
    toast.success("Roster updated successfully.");
  };

  const addException = async () => {
    if (!selectedCounsellor || !newException.date) {
      return;
    }
    const payload = {
      ...newException,
      counsellor_id: selectedCounsellor,
      is_available: Boolean(newException.is_available),
      override_start_time: newException.override_start_time || null,
      override_end_time: newException.override_end_time || null,
    };
    const data = await appointmentsApi.createException(payload);
    if (data?.error) {
      toast.error(data.error);
      return;
    }
    toast.success("Availability exception added.");
    setNewException({ date: "", is_available: false, override_start_time: "", override_end_time: "", reason: "" });
    loadExceptions();
  };

  const connectOutlook = async () => {
    setOutlookBusy(true);
    const payload = {
      email: outlookForm.email,
      tenant_id: outlookForm.tenant_id || null,
      calendar_id: outlookForm.calendar_id || null,
      access_token: outlookForm.access_token || null,
      refresh_token: outlookForm.refresh_token || null,
      token_expires_at: outlookForm.token_expires_at || null,
    };
    const data = await appointmentsApi.connectOutlook(payload);
    setOutlookBusy(false);
    if (data?.error) {
      toast.error(data.error);
      return;
    }
    toast.success("Outlook calendar connected.");
    loadOutlookStatus();
  };

  const syncOutlook = async () => {
    setOutlookBusy(true);
    const data = await appointmentsApi.syncOutlook();
    setOutlookBusy(false);
    if (data?.error) {
      toast.error(data.error);
      return;
    }
    toast.success(`Outlook sync completed (${data?.result?.synced_count || 0} appointments).`);
    loadOutlookStatus();
  };

  const disconnectOutlook = async () => {
    setOutlookBusy(true);
    const data = await appointmentsApi.disconnectOutlook();
    setOutlookBusy(false);
    if (data?.error) {
      toast.error(data.error);
      return;
    }
    toast.success("Outlook calendar disconnected.");
    loadOutlookStatus();
  };

  const runReminderSweep = async () => {
    setReminderBusy(true);
    const data = await appointmentsApi.runReminderSweep();
    setReminderBusy(false);
    if (data?.error) {
      toast.error(data.error);
      return;
    }
    const result = data?.result || {};
    toast.success(
      `Reminder sweep complete. Sent: ${result.sent_count || 0}, skipped: ${result.skipped_count || 0}.`
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.contentCol}>
        <div className={styles.card}>
            <div className={styles.header}>
              <div>
                <h4 className={styles.title}>Availability Settings</h4>
                <small className={styles.subtitle}>Your roster, capacity, buffers and holiday overrides</small>
              </div>
              <div className={styles.toolbar}>
                <Form.Control value={timezone} onChange={(e) => setTimezone(e.target.value)} />
                <Button onClick={saveRoster}>Save Roster</Button>
              </div>
            </div>

            <div className={styles.section}>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Day</th>
                    <th>Enabled</th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Slot (min)</th>
                    <th>Buffer Before</th>
                    <th>Buffer After</th>
                    <th>Max Concurrent</th>
                  </tr>
                </thead>
                <tbody>
                  {weekdays.map((day) => {
                    const rule = rules.find((item) => item.weekday === day.id) || makeRule(day);
                    return (
                      <tr key={day.id}>
                        <td>{day.label}</td>
                        <td>
                          <Form.Check
                            type="switch"
                            checked={Boolean(rule.enabled)}
                            onChange={(e) => updateRule(day.id, "enabled", e.target.checked)}
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="time"
                            value={rule.start_time}
                            onChange={(e) => updateRule(day.id, "start_time", e.target.value)}
                            disabled={!rule.enabled}
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="time"
                            value={rule.end_time}
                            onChange={(e) => updateRule(day.id, "end_time", e.target.value)}
                            disabled={!rule.enabled}
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="number"
                            value={rule.slot_duration_minutes}
                            onChange={(e) => updateRule(day.id, "slot_duration_minutes", e.target.value)}
                            min={5}
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="number"
                            value={rule.buffer_before_minutes}
                            onChange={(e) => updateRule(day.id, "buffer_before_minutes", e.target.value)}
                            min={0}
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="number"
                            value={rule.buffer_after_minutes}
                            onChange={(e) => updateRule(day.id, "buffer_after_minutes", e.target.value)}
                            min={0}
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="number"
                            value={rule.max_concurrent_bookings}
                            onChange={(e) => updateRule(day.id, "max_concurrent_bookings", e.target.value)}
                            min={1}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>

            <div className={styles.sectionBorder}>
              <h6 className={styles.sectionTitle}>Holiday Overrides / Blocked Dates</h6>
              <Row className="g-2 mb-3">
                <Col md={2}>
                  <Form.Control
                    type="date"
                    value={newException.date}
                    onChange={(e) => setNewException((prev) => ({ ...prev, date: e.target.value }))}
                  />
                </Col>
                <Col md={2}>
                  <Form.Select
                    value={String(newException.is_available)}
                    onChange={(e) =>
                      setNewException((prev) => ({ ...prev, is_available: e.target.value === "true" }))
                    }
                  >
                    <option value="false">Blocked</option>
                    <option value="true">Available</option>
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Control
                    type="time"
                    value={newException.override_start_time}
                    onChange={(e) =>
                      setNewException((prev) => ({ ...prev, override_start_time: e.target.value }))
                    }
                  />
                </Col>
                <Col md={2}>
                  <Form.Control
                    type="time"
                    value={newException.override_end_time}
                    onChange={(e) => setNewException((prev) => ({ ...prev, override_end_time: e.target.value }))}
                  />
                </Col>
                <Col md={3}>
                  <Form.Control
                    value={newException.reason}
                    onChange={(e) => setNewException((prev) => ({ ...prev, reason: e.target.value }))}
                    placeholder="Reason"
                  />
                </Col>
                <Col md={1}>
                  <Button onClick={addException}>Add</Button>
                </Col>
              </Row>
              <Table responsive>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Window</th>
                    <th>Reason</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {exceptions.length === 0 ? (
                    <tr>
                      <td colSpan={5} className={styles.muted}>
                        No exceptions created.
                      </td>
                    </tr>
                  ) : (
                    exceptions.map((item) => (
                      <tr key={item.id}>
                        <td>{item.date}</td>
                        <td>{item.is_available ? "Available Override" : "Blocked"}</td>
                        <td>
                          {item.override_start_time || "--"} to {item.override_end_time || "--"}
                        </td>
                        <td>{item.reason || "--"}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={async () => {
                              await appointmentsApi.deleteException(item.id);
                              loadExceptions();
                            }}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>

            <div className={styles.sectionBorder}>
              <h6 className={styles.sectionTitle}>Outlook Calendar Integration</h6>
              <p className={styles.muted}>
                Connect your Outlook calendar to keep appointment sync metadata updated.
              </p>
              <Row className="g-2 mb-3">
                <Col md={4}>
                  <Form.Control
                    placeholder="Outlook email"
                    value={outlookForm.email}
                    onChange={(e) => setOutlookForm((prev) => ({ ...prev, email: e.target.value }))}
                  />
                </Col>
                <Col md={4}>
                  <Form.Control
                    placeholder="Tenant ID"
                    value={outlookForm.tenant_id}
                    onChange={(e) => setOutlookForm((prev) => ({ ...prev, tenant_id: e.target.value }))}
                  />
                </Col>
                <Col md={4}>
                  <Form.Control
                    placeholder="Calendar ID"
                    value={outlookForm.calendar_id}
                    onChange={(e) => setOutlookForm((prev) => ({ ...prev, calendar_id: e.target.value }))}
                  />
                </Col>
                <Col md={6}>
                  <Form.Control
                    placeholder="Access token"
                    value={outlookForm.access_token}
                    onChange={(e) => setOutlookForm((prev) => ({ ...prev, access_token: e.target.value }))}
                  />
                </Col>
                <Col md={6}>
                  <Form.Control
                    placeholder="Refresh token"
                    value={outlookForm.refresh_token}
                    onChange={(e) => setOutlookForm((prev) => ({ ...prev, refresh_token: e.target.value }))}
                  />
                </Col>
                <Col md={6}>
                  <Form.Control
                    type="datetime-local"
                    value={outlookForm.token_expires_at}
                    onChange={(e) => setOutlookForm((prev) => ({ ...prev, token_expires_at: e.target.value }))}
                  />
                </Col>
              </Row>
              <div className={styles.toolbar}>
                <Button onClick={connectOutlook} disabled={outlookBusy}>
                  {outlookBusy ? "Connecting..." : "Connect Outlook"}
                </Button>
                <Button variant="outline-primary" onClick={syncOutlook} disabled={outlookBusy}>
                  {outlookBusy ? "Syncing..." : "Sync Now"}
                </Button>
                <Button variant="outline-danger" onClick={disconnectOutlook} disabled={outlookBusy}>
                  Disconnect
                </Button>
                <Button variant="outline-secondary" onClick={runReminderSweep} disabled={reminderBusy}>
                  {reminderBusy ? "Running..." : "Run 1h Reminder Sweep"}
                </Button>
              </div>
              <div className={styles.statusRow}>
                <strong>Status:</strong>{" "}
                {outlookStatus?.sync_enabled ? "Connected" : "Not connected"}{" "}
                {outlookStatus?.last_synced_at ? `(Last sync: ${outlookStatus.last_synced_at})` : ""}
              </div>
            </div>
          </div>
      </div>
    </div>
  );
}
